import { useState } from 'react';
import { clsx } from 'clsx';
import type { Alignment } from '@character-creator/types';
import { useCharacterCreationStore } from '../../../stores/characterCreationStore';
import { generateRandomName } from '../../../utils/randomCharacter';
import { DiceIcon } from '../../ui/Icons';

const ALIGNMENTS: { value: Alignment; label: string }[] = [
  { value: 'lawful-good', label: 'Lawful Good' },
  { value: 'neutral-good', label: 'Neutral Good' },
  { value: 'chaotic-good', label: 'Chaotic Good' },
  { value: 'lawful-neutral', label: 'Lawful Neutral' },
  { value: 'true-neutral', label: 'True Neutral' },
  { value: 'chaotic-neutral', label: 'Chaotic Neutral' },
  { value: 'lawful-evil', label: 'Lawful Evil' },
  { value: 'neutral-evil', label: 'Neutral Evil' },
  { value: 'chaotic-evil', label: 'Chaotic Evil' },
];

export function BasicsStep() {
  const { draft, setName, setAlignment, setPlayerName, setCampaignName, clearBasics } = useCharacterCreationStore();
  const [alignmentExpanded, setAlignmentExpanded] = useState(draft.alignment !== 'true-neutral');

  const handleRandomName = () => {
    setName(generateRandomName());
  };

  const hasData = draft.name.trim() !== '' ||
    draft.alignment !== 'true-neutral';

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
            Character Basics
          </h2>
          <p className="text-text-secondary">
            Name your character and define their core identity.
          </p>
        </div>
        {hasData && (
          <button
            type="button"
            onClick={clearBasics}
            className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Name */}
        <div className="card p-6">
          <label htmlFor="character-name" className="block text-sm font-medium text-text-secondary mb-2">
            Character Name
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              id="character-name"
              value={draft.name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your character's name..."
              className="input text-lg flex-1"
              autoFocus
              maxLength={50}
            />
            <button
              type="button"
              onClick={handleRandomName}
              className="px-4 py-2 rounded-lg bg-background-secondary text-text-primary hover:bg-background-elevated transition-colors flex items-center gap-2"
              title="Generate random name"
            >
              <DiceIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Random</span>
            </button>
          </div>
        </div>

        {/* Alignment */}
        <div className="card p-6">
          <button
            type="button"
            onClick={() => setAlignmentExpanded(!alignmentExpanded)}
            aria-expanded={alignmentExpanded}
            className="w-full flex items-center justify-between"
          >
            <h3 className="font-semibold text-text-primary">
              Alignment <span className="text-text-muted font-normal">(optional)</span>
            </h3>
            <div className="flex items-center gap-2">
              {!alignmentExpanded && draft.alignment !== 'true-neutral' && (
                <span className="text-sm text-accent-gold">
                  {ALIGNMENTS.find(a => a.value === draft.alignment)?.label}
                </span>
              )}
              <span className={clsx(
                'text-text-muted text-xs transition-transform duration-200',
                alignmentExpanded && 'rotate-90'
              )}>
                ▶
              </span>
            </div>
          </button>
          <div className={clsx(
            'overflow-hidden transition-all duration-200 ease-in-out',
            alignmentExpanded ? 'max-h-[200px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          )}>
            <div className="grid grid-cols-3 gap-2">
              {ALIGNMENTS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAlignment(value)}
                  className={clsx(
                    'p-2 text-sm rounded-lg border transition-all duration-200 text-center',
                    draft.alignment === value
                      ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
                      : 'bg-background-secondary border-background-elevated text-text-primary hover:border-text-muted'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Campaign Info */}
        <div className="card p-6">
          <h3 className="font-semibold text-text-primary mb-4">Campaign Info</h3>
          <p className="text-sm text-text-muted mb-4">
            Optional fields to help organize characters across campaigns.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="player-name" className="block text-sm text-text-muted mb-1">
                Player Name
              </label>
              <input
                type="text"
                id="player-name"
                value={draft.playerName || ''}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Your name"
                className="input"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="campaign-name" className="block text-sm text-text-muted mb-1">
                Campaign Name
              </label>
              <input
                type="text"
                id="campaign-name"
                value={draft.campaignName || ''}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g., The Lost Mines"
                className="input"
                maxLength={100}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


